import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * MiniWIA - Mini W IA embutido para Wchat
 * Visual Liquid Glass inspirado nas mensagens criptografadas
 */
const MiniWIA = ({ currentMember, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  const quickSuggestions = [
    'Roteiro de luxo na Toscana',
    'Melhores hotéis 5 estrelas',
    'Harmonização de vinhos',
    'Relógios de alta relojoaria',
    'Charutos cubanos premium',
    'Experiências gastronômicas',
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingIndicator]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  useEffect(() => {
    const name = currentMember?.name?.split(' ')[0] || '';
    setMessages([{
      role: 'assistant',
      content: `Olá ${name}! Sou o W, seu consultor exclusivo. Como posso ajudar você hoje?`
    }]);
  }, [currentMember]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;
    
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setTypingIndicator(true);

    try {
      const response = await axios.post(`${API}/sommelier`, {
        member_id: currentMember?.id,
        message: text
      });

      const aiResponse = response.data.response;
      setTypingIndicator(false);
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('[MiniWIA] Error:', error);
      setTypingIndicator(false);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Desculpe, ocorreu um erro. Tente novamente.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const formatTime = () => {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="mini-wia-modal"
    >
      {/* Bottom Sheet Container */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="mt-auto flex flex-col rounded-t-3xl overflow-hidden"
        style={{ 
          background: 'linear-gradient(180deg, rgba(15,10,30,0.98) 0%, rgba(5,5,15,0.99) 100%)',
          maxHeight: '85vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Liquid Glass Header */}
        <div
          className="flex-shrink-0 mx-4 px-4 py-3 rounded-2xl flex items-center justify-between backdrop-blur-xl"
          style={{ 
            background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(59,130,246,0.1) 50%, rgba(139,92,246,0.08) 100%)',
            boxShadow: '0 8px 32px rgba(139,92,246,0.12), inset 0 1px 0 rgba(255,255,255,0.04)'
          }}
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ 
                textShadow: [
                  '0 0 15px rgba(139,92,246,0.5)',
                  '0 0 25px rgba(139,92,246,0.7)',
                  '0 0 15px rgba(139,92,246,0.5)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-10 h-10 flex items-center justify-center"
            >
              <span className="text-xl font-['Jost'] font-bold bg-gradient-to-r from-violet-400 to-blue-400 bg-clip-text text-transparent">W</span>
            </motion.div>
            <div>
              <h3 className="text-sm font-['Jost'] font-semibold text-white/90">W Luxury Expert</h3>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                <p className="text-[10px] text-white/50">Online agora</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Expand icon */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: { tab: 'wia' } }));
                setTimeout(() => onClose(), 100);
              }}
              className="flex items-center justify-center transition"
              data-testid="mini-wia-expand-btn"
            >
              <svg className="w-5 h-5 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </motion.button>
            
            {/* Close X */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="text-white/40 hover:text-white/70 transition"
              data-testid="mini-wia-close-btn"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </motion.button>
          </div>
        </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="mini-wia-messages">
        {messages.map((msg, index) => (
          <motion.div
            key={`mini-msg-${index}-${msg.role}-${msg.content?.substring(0, 15) || 'empty'}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] ${msg.role === 'user' ? '' : 'flex items-start gap-2'}`}>
              {/* Avatar W - só texto, sem fundo */}
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mt-1">
                  <span className="text-sm font-['Jost'] font-bold text-violet-400">W</span>
                </div>
              )}
              
              {/* Message Bubble - Liquid Glass */}
              <div 
                className={`px-4 py-3 rounded-2xl backdrop-blur-xl ${
                  msg.role === 'user' ? 'rounded-br-[4px]' : 'rounded-bl-[4px]'
                }`}
                style={msg.role === 'user' ? {
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.2) 0%, rgba(59,130,246,0.15) 100%)',
                  border: '1px solid rgba(139,92,246,0.3)',
                  boxShadow: '0 4px 20px rgba(139,92,246,0.1), inset 0 1px 0 rgba(255,255,255,0.05)'
                } : {
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)'
                }}
              >
                <p className="text-[13px] leading-relaxed text-white/85 font-['Jost'] whitespace-pre-wrap">
                  {msg.content}
                </p>
                <p className="text-[9px] text-white/30 mt-1.5 text-right font-['Jost']">
                  {formatTime()}
                </p>
              </div>
            </div>
          </motion.div>
        ))}

        {/* Typing indicator - Liquid Glass */}
        {typingIndicator && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mt-1">
                <span className="text-sm font-['Jost'] font-bold text-violet-400">W</span>
              </div>
              <div 
                className="px-4 py-3 rounded-2xl rounded-bl-[4px] backdrop-blur-sm"
                style={{ 
                  background: 'rgba(255,255,255,0.03)', 
                  border: '1px solid rgba(255,255,255,0.06)' 
                }}
              >
                <div className="flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      className="w-1.5 h-1.5 rounded-full bg-violet-400/60"
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Suggestions - Liquid Glass Pills */}
      {messages.length === 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="px-4 pb-2"
        >
          <p className="text-[10px] text-white/30 mb-2 font-['Jost']">Sugestões rápidas:</p>
          <div className="flex flex-wrap gap-2">
            {quickSuggestions.map((suggestion, index) => (
              <motion.button
                key={`suggestion-${suggestion.substring(0, 20)}`}
                whileTap={{ scale: 0.95 }}
                onClick={() => sendMessage(suggestion)}
                className="px-3 py-1.5 rounded-full text-[11px] font-['Jost'] text-white/60 hover:text-white/90 transition backdrop-blur-sm"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(59,130,246,0.08) 100%)',
                  border: '1px solid rgba(139,92,246,0.2)' 
                }}
                data-testid={`mini-wia-suggestion-${index}`}
              >
                {suggestion}
              </motion.button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Input Area - Liquid Glass */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="flex-shrink-0 px-4 pb-6 pt-2"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom, 1.5rem))' }}
      >
        <form onSubmit={handleSubmit}>
          <div 
            className="flex items-center gap-2 p-2 rounded-2xl backdrop-blur-xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(59,130,246,0.05) 100%)',
              border: '1px solid rgba(139,92,246,0.15)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.03)'
            }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pergunte ao W..."
              disabled={isLoading}
              className="flex-1 px-3 py-2.5 bg-transparent text-sm text-white/90 placeholder:text-white/30 outline-none font-['Jost']"
              data-testid="mini-wia-input"
            />
            
            <motion.button
              type="submit"
              disabled={!input.trim() || isLoading}
              whileTap={{ scale: 0.9 }}
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-30 backdrop-blur-sm"
              style={{ 
                background: input.trim() 
                  ? 'linear-gradient(145deg, rgba(139,92,246,0.6) 0%, rgba(59,130,246,0.5) 100%)'
                  : 'rgba(255,255,255,0.05)',
                border: input.trim() 
                  ? '1px solid rgba(139,92,246,0.5)'
                  : '1px solid rgba(255,255,255,0.1)',
                boxShadow: input.trim() ? '0 4px 15px rgba(139,92,246,0.3)' : 'none'
              }}
              data-testid="mini-wia-send-btn"
            >
              {isLoading ? (
                <svg className="w-4 h-4 animate-spin text-white/50" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                <svg className={`w-4 h-4 ${input.trim() ? 'text-white' : 'text-white/30'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              )}
            </motion.button>
          </div>
        </form>
        
        <p className="text-center text-[9px] text-white/20 mt-3 pb-2 font-['Jost'] tracking-wider">
          Powered by W IA
        </p>
      </motion.div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default MiniWIA;
